import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Scissors, Info, Image as ImageIcon } from "lucide-react";
import type { SlicingModelInfo } from "@/api/pizza-checker/schemas";
import { useLocalization } from "@/context/localization-context";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";
import { SLICE_COLORS, displaySlices, type SlicingFrame, type SlicingViewMode } from "@/lib/slicing";

/**
 * Slicing layer (backend metadata.slicing_model, see pizza-backend vision.slicing_cnn): every slice is the
 * region between two neighbouring cut crossings on the rim and the cuts' common point (apex). Slices smaller
 * than metadata.min_fraction of the pizza are hatched red, the others green. Informational only.
 *
 * All geometry is in source-image pixels; the SVG viewBox is the same frame the photo canvas shows.
 */

const rad = (deg: number) => (deg * Math.PI) / 180;

/** Hatch pattern: tinted base + diagonal stripes; the "small" one drifts slowly to draw the eye. */
function HatchPattern({
  id,
  color,
  spacing,
  angle,
  base,
  stripe,
  drift,
}: {
  id: string;
  color: string;
  spacing: number;
  angle: number;
  base: number;
  stripe: number;
  drift: boolean;
}) {
  return (
    <pattern
      id={id}
      patternUnits="userSpaceOnUse"
      width={spacing}
      height={spacing}
      patternTransform={`rotate(${angle})`}
    >
      <rect width={spacing} height={spacing} fill={color} fillOpacity={base} />
      <g>
        <rect x={0} y={0} width={spacing * 0.2} height={spacing} fill={color} fillOpacity={stripe} />
        <rect x={spacing} y={0} width={spacing * 0.2} height={spacing} fill={color} fillOpacity={stripe} />
        {drift && (
          <animateTransform
            attributeName="transform"
            type="translate"
            from="0 0"
            to={`${-spacing} 0`}
            dur="2.6s"
            repeatCount="indefinite"
          />
        )}
      </g>
    </pattern>
  );
}

export function SlicingOverlay({
  slicing,
  frame,
  hovered,
  onHover,
  mode = "overlay",
  className,
}: {
  slicing: SlicingModelInfo;
  frame: SlicingFrame;
  hovered: number | null;
  onHover: (sliceNumber: number | null) => void;
  /** overlay = hatched slices, cuts and labels; source = the bare photo with crossing ticks outside the rim */
  mode?: SlicingViewMode;
  className?: string;
}) {
  const { t, formatNumber } = useLocalization();
  const interactive = mode === "overlay";
  const uid = useId().replace(/:/g, "");
  const reduced = usePrefersReducedMotion();
  const circle = slicing.circle;
  const slices = useMemo(() => displaySlices(slicing), [slicing]);

  if (!circle) return null;
  const { x: cx, y: cy, r: R } = circle;
  const apex = slicing.apex ?? { x: cx, y: cy };
  const rim = (deg: number) => ({ x: cx + R * Math.cos(rad(deg)), y: cy + R * Math.sin(rad(deg)) });
  const hatch = Math.max(8, R * 0.055);
  const cutWidth = Math.max(2, R * 0.011);
  const fontSize = Math.max(10, R * 0.056);
  const sliced = slicing.status === "sliced" && slices.length > 0;
  const circumference = 2 * Math.PI * R;

  return (
    <svg
      viewBox={`${frame.x} ${frame.y} ${frame.width} ${frame.height}`}
      preserveAspectRatio="xMidYMid meet"
      className={cn("slicing-motion pointer-events-none absolute inset-0 h-full w-full", className)}
      aria-label={t("slicing.title")}
    >
      <defs>
        <HatchPattern
          id={`${uid}-ok`}
          color={SLICE_COLORS.ok}
          spacing={hatch}
          angle={45}
          base={0.08}
          stripe={0.62}
          drift={false}
        />
        <HatchPattern
          id={`${uid}-small`}
          color={SLICE_COLORS.small}
          spacing={hatch * 0.85}
          angle={-45}
          base={0.16}
          stripe={0.8}
          drift={!reduced}
        />
        <mask id={`${uid}-outside`}>
          <rect x={frame.x} y={frame.y} width={frame.width} height={frame.height} fill="white" />
          <circle cx={cx} cy={cy} r={R} fill="black" />
        </mask>
        <filter id={`${uid}-glow`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation={cutWidth * 0.9} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`${uid}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy={fontSize * 0.08} stdDeviation={fontSize * 0.12} floodOpacity="0.35" />
        </filter>
      </defs>

      {/* everything drawn over the pizza; fades out in the source view */}
      <g
        className="transition-opacity duration-500 ease-out"
        style={{ opacity: interactive ? 1 : 0 }}
        aria-hidden={!interactive}
      >

      {/* focus: dim everything outside the pizza */}
      <rect
        x={frame.x}
        y={frame.y}
        width={frame.width}
        height={frame.height}
        fill="#0b0f19"
        mask={`url(#${uid}-outside)`}
        className="slicing-veil"
      />

      {/* rim */}
      <circle
        cx={cx}
        cy={cy}
        r={R}
        fill="none"
        stroke="white"
        strokeOpacity={0.7}
        strokeWidth={cutWidth * 0.55}
        strokeDasharray={circumference}
        strokeDashoffset={circumference}
        transform={`rotate(-90 ${cx} ${cy})`}
        className="slicing-rim"
        style={{ ["--slicing-len" as string]: `${circumference}` }}
      />

      {sliced && (
        <g>
          {slices.map((s, i) => {
            const p0 = rim(s.startAngle);
            const p1 = rim(s.endAngle);
            const large = s.sweep > 180 ? 1 : 0;
            const d = `M 0 0 L ${p0.x - apex.x} ${p0.y - apex.y} A ${R} ${R} 0 ${large} 1 ${p1.x - apex.x} ${p1.y - apex.y} Z`;
            const isHovered = hovered === s.number;
            const dimmed = hovered !== null && !isHovered;
            return (
              <g key={s.number} transform={`translate(${apex.x} ${apex.y})`}>
                <g className="slicing-slice" style={{ animationDelay: `${450 + i * 70}ms` }}>
                  <path
                    d={d}
                    fill={`url(#${uid}-${s.status})`}
                    stroke="white"
                    strokeOpacity={isHovered ? 0.95 : 0.5}
                    strokeWidth={isHovered ? cutWidth * 0.9 : cutWidth * 0.35}
                    strokeLinejoin="round"
                    className={cn(
                      interactive && "pointer-events-auto cursor-pointer",
                      "transition-[opacity,stroke-width] duration-300",
                      s.status === "small" && !isHovered && "slicing-small-pulse",
                    )}
                    style={{ opacity: dimmed ? 0.35 : isHovered ? 1 : 0.88 }}
                    onMouseEnter={() => onHover(s.number)}
                    onMouseLeave={() => onHover(null)}
                  />
                </g>
              </g>
            );
          })}
        </g>
      )}

      {/* cuts: drawn from the rim to the apex */}
      {(slicing.cuts ?? []).map((c, i) => (
        <g key={`cut-${i}`}>
          <path
            d={`M ${c.x} ${c.y} L ${apex.x} ${apex.y}`}
            pathLength={1}
            stroke="white"
            strokeWidth={cutWidth}
            strokeLinecap="round"
            fill="none"
            filter={`url(#${uid}-glow)`}
            className="slicing-cut"
            style={{ animationDelay: `${150 + i * 60}ms` }}
          />
          <g transform={`translate(${c.x} ${c.y})`}>
            <circle
              r={cutWidth * 2.1}
              fill="white"
              stroke="#0f172a"
              strokeOpacity={0.55}
              strokeWidth={cutWidth * 0.5}
              className="slicing-pop"
              style={{ animationDelay: `${120 + i * 60}ms` }}
            />
          </g>
        </g>
      ))}

      {/* apex */}
      {sliced && (
        <g transform={`translate(${apex.x} ${apex.y})`}>
          {!reduced && (
            <circle r={cutWidth * 2.4} fill="none" stroke="white" strokeWidth={cutWidth * 0.5} className="slicing-ripple" />
          )}
          <circle r={cutWidth * 1.6} fill="white" className="slicing-pop" style={{ animationDelay: "700ms" }} />
        </g>
      )}

      {/* area labels */}
      {sliced &&
        slices.map((s, i) => {
          const mid = s.startAngle + s.sweep / 2;
          const pm = rim(mid);
          const k = s.fraction < 0.07 ? 0.8 : 0.62;
          const lx = apex.x + (pm.x - apex.x) * k;
          const ly = apex.y + (pm.y - apex.y) * k;
          const isHovered = hovered === s.number;
          const text = formatNumber(s.fraction, { style: "percent", maximumFractionDigits: 1, minimumFractionDigits: 1 });
          const w = fontSize * (0.62 * text.length + 0.9);
          const h = fontSize * 1.55;
          return (
            <g key={`label-${s.number}`} transform={`translate(${lx} ${ly})`}>
              <g className="slicing-label" style={{ animationDelay: `${900 + i * 60}ms` }}>
                <g
                  className="transition-transform duration-300"
                  style={{ transform: `scale(${isHovered ? 1.22 : 1})` }}
                  filter={`url(#${uid}-shadow)`}
                >
                  <rect
                    x={-w / 2}
                    y={-h / 2}
                    width={w}
                    height={h}
                    rx={h / 2}
                    fill={SLICE_COLORS[s.status]}
                    fillOpacity={0.94}
                    stroke="white"
                    strokeOpacity={0.9}
                    strokeWidth={fontSize * 0.09}
                  />
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="white"
                    fontSize={fontSize}
                    fontWeight={700}
                    style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.01em" }}
                  >
                    {text}
                  </text>
                </g>
              </g>
            </g>
          );
        })}

      {/* uncut: a slowly turning dashed ring */}
      {slicing.status === "uncut" && (
        <g transform={`translate(${cx} ${cy})`}>
          <g className="slicing-spin">
            <circle
              r={R * 0.985}
              fill="none"
              stroke="white"
              strokeOpacity={0.85}
              strokeWidth={cutWidth * 0.8}
              strokeDasharray={`${R * 0.06} ${R * 0.045}`}
              strokeLinecap="round"
            />
          </g>
          <g className="slicing-label" style={{ animationDelay: "400ms" }} filter={`url(#${uid}-shadow)`}>
            <rect
              x={-fontSize * 4.4}
              y={-fontSize * 0.95}
              width={fontSize * 8.8}
              height={fontSize * 1.9}
              rx={fontSize * 0.95}
              fill="#0f172a"
              fillOpacity={0.78}
              stroke="white"
              strokeOpacity={0.35}
              strokeWidth={fontSize * 0.06}
            />
            <text textAnchor="middle" dominantBaseline="central" fill="white" fontSize={fontSize * 0.95} fontWeight={600}>
              {t("slicing.uncut")}
            </text>
          </g>
        </g>
      )}
      </g>

      {/* source view: only where the cuts cross the crust, as ticks outside the rim (nothing over the pizza) */}
      <g
        className="transition-opacity duration-500 ease-out"
        style={{ opacity: interactive ? 0 : 1 }}
        aria-hidden={interactive}
      >
        {(slicing.cuts ?? []).map((c, i) => {
          const a = rad(c.angle);
          const p = (k: number) => `${cx + k * R * Math.cos(a)} ${cy + k * R * Math.sin(a)}`;
          return (
            <g key={`tick-${i}`}>
              <path
                d={`M ${p(1.015)} L ${p(1.12)}`}
                stroke="#0f172a"
                strokeOpacity={0.75}
                strokeWidth={cutWidth * 2.6}
                strokeLinecap="round"
              />
              <path d={`M ${p(1.015)} L ${p(1.12)}`} stroke="white" strokeWidth={cutWidth * 1.3} strokeLinecap="round" />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/** Compact chip in the photo's bottom-left corner (outside the pizza disc). */
export function SlicingBadge({ slicing, className }: { slicing: SlicingModelInfo; className?: string }) {
  const { t } = useLocalization();
  const small = slicing.small_count ?? 0;
  return (
    <div
      className={cn(
        "slicing-legend pointer-events-none absolute bottom-3 left-3 z-30 flex items-center gap-2 rounded-full",
        "border border-white/15 bg-slate-950/70 py-1 pl-1 pr-2.5 text-xs text-white shadow-xl backdrop-blur-md",
        className,
      )}
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15">
        <Scissors className="h-3.5 w-3.5" />
      </span>
      {slicing.status === "sliced" ? (
        <>
          <span className="font-medium tabular-nums">{t("slicing.sliceCount", { count: slicing.slice_count ?? 0 })}</span>
          <span
            className={cn(
              "rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
              small > 0 ? "bg-red-600 text-white" : "bg-emerald-600 text-white",
            )}
          >
            {small > 0 ? t("slicing.smallShort", { count: small }) : t("slicing.allOk")}
          </span>
        </>
      ) : (
        <span className="font-medium">{slicing.status === "uncut" ? t("slicing.uncut") : t("slicing.skipped")}</span>
      )}
    </div>
  );
}

function Swatch({ status }: { status: "ok" | "small" }) {
  const id = useId().replace(/:/g, "");
  const color = SLICE_COLORS[status];
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0 overflow-visible">
      <defs>
        <pattern
          id={id}
          patternUnits="userSpaceOnUse"
          width="4"
          height="4"
          patternTransform={`rotate(${status === "ok" ? 45 : -45})`}
        >
          <rect width="4" height="4" fill={color} fillOpacity={0.15} />
          <rect width="1.4" height="4" fill={color} />
        </pattern>
      </defs>
      <rect x="0.5" y="0.5" width="15" height="15" rx="4" fill={`url(#${id})`} stroke={color} strokeWidth="1" />
    </svg>
  );
}

/** Panel under the photo: summary (counts, target, hovered slice) next to the slice-area chart. */
export function SlicingPanel({
  slicing,
  hovered,
  onHover,
}: {
  slicing: SlicingModelInfo;
  hovered: number | null;
  onHover: (sliceNumber: number | null) => void;
}) {
  const { t, formatNumber } = useLocalization();
  const slices = useMemo(() => displaySlices(slicing), [slicing]);
  const n = slicing.expected_slices;
  const pct = (v: number, digits = 1) =>
    formatNumber(v * 100, { maximumFractionDigits: digits, minimumFractionDigits: digits });
  const okCount = slices.filter((s) => s.status === "ok").length;
  const smallCount = slices.length - okCount;
  const hoveredSlice = hovered !== null ? slices.find((s) => s.number === hovered) : undefined;
  const sliced = slicing.status === "sliced" && slices.length > 0;

  return (
    <div className="slicing-legend grid gap-4 border-t px-4 pt-4 md:grid-cols-[13.5rem_1fr]">
      <div className="flex flex-col">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted ring-1 ring-border">
            <Scissors className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-semibold leading-tight">{t("slicing.title")}</div>
            <div className="text-xs text-muted-foreground">
              {sliced
                ? t("slicing.sliceCount", { count: slices.length })
                : slicing.status === "uncut"
                  ? t("slicing.uncut")
                  : t("slicing.skipped")}
            </div>
          </div>
        </div>

        {sliced && (
          <>
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex items-center gap-2">
                <Swatch status="ok" />
                <span className="flex-1 text-muted-foreground">{t("slicing.ok")}</span>
                <span className="font-semibold tabular-nums">{okCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <Swatch status="small" />
                <span className="flex-1 text-muted-foreground">{t("slicing.small", { n })}</span>
                <span className={cn("font-semibold tabular-nums", smallCount > 0 && "text-red-600 dark:text-red-400")}>
                  {smallCount}
                </span>
              </div>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {t("slicing.target", {
                target: pct(1 / n),
                min: pct(slicing.min_fraction),
                tol: formatNumber(slicing.tolerance * 100, { maximumFractionDigits: 0 }),
              })}
            </div>
            <div
              className={cn(
                "grid transition-all duration-300 ease-out",
                hoveredSlice ? "mt-2 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
              )}
            >
              <div className="overflow-hidden">
                {hoveredSlice && (
                  <div className="flex items-center justify-between rounded-lg bg-muted px-2.5 py-1.5 text-xs ring-1 ring-border">
                    <span className="text-muted-foreground">{t("slicing.slice", { n: hoveredSlice.number })}</span>
                    <span className="font-semibold tabular-nums">
                      {formatNumber(hoveredSlice.fraction, {
                        style: "percent",
                        maximumFractionDigits: 1,
                        minimumFractionDigits: 1,
                      })}
                      <span
                        className={cn(
                          "ml-1.5 font-medium",
                          hoveredSlice.status === "small"
                            ? "text-red-600 dark:text-red-400"
                            : "text-emerald-700 dark:text-emerald-400",
                        )}
                      >
                        {hoveredSlice.fraction * n >= 1 ? "+" : "−"}
                        {formatNumber(Math.abs(hoveredSlice.fraction * n - 1), {
                          style: "percent",
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {slicing.status === "skipped" && slicing.skipped && (
          <div className="mt-2 text-xs text-muted-foreground">{slicing.skipped}</div>
        )}

        <div className="mt-auto flex items-center gap-1.5 pt-3 text-[11px] text-muted-foreground/80">
          <Info className="h-3 w-3 shrink-0" />
          <span className="flex-1">{t("slicing.infoOnly")}</span>
          {slicing.version && <span className="tabular-nums">{slicing.version}</span>}
        </div>
      </div>

      {sliced && <SlicingChart slicing={slicing} hovered={hovered} onHover={onHover} />}
    </div>
  );
}

/** Column chart of the slice areas with the target band; hover syncs with the overlay. */
function SlicingChart({
  slicing,
  hovered,
  onHover,
}: {
  slicing: SlicingModelInfo;
  hovered: number | null;
  onHover: (sliceNumber: number | null) => void;
}) {
  const { t, formatNumber } = useLocalization();
  const slices = useMemo(() => displaySlices(slicing), [slicing]);
  const n = slicing.expected_slices;
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    setGrown(false);
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setGrown(true)));
    return () => cancelAnimationFrame(id);
  }, [slicing]);

  const target = 1 / n;
  const upper = (1 + slicing.tolerance) / n;
  const max = Math.max(upper, ...slices.map((s) => s.fraction)) * 1.18;
  const y = (v: number) => `${(v / max) * 100}%`;

  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-sm font-medium">{t("slicing.distribution")}</div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block h-2 w-4 rounded-sm border border-dashed border-emerald-500/60 bg-emerald-500/15" />
          {t("slicing.targetShort", {
            target: formatNumber(target * 100, { maximumFractionDigits: 1, minimumFractionDigits: 1 }),
          })}
        </div>
      </div>
      <div className="relative h-36 rounded-lg border bg-muted/30 px-2">
        {/* target band (min..max area) and the 1/n line */}
        <div
          className="absolute inset-x-0 border-y border-dashed border-emerald-500/50 bg-emerald-500/10"
          style={{ bottom: y(slicing.min_fraction), height: `calc(${y(upper)} - ${y(slicing.min_fraction)})` }}
        />
        <div className="absolute inset-x-0 border-t border-emerald-600/70" style={{ bottom: y(target) }} />
        <div className="relative flex h-full items-end gap-1.5">
          {slices.map((s, i) => {
            const color = SLICE_COLORS[s.status];
            const isHovered = hovered === s.number;
            return (
              <div
                key={s.number}
                className="relative flex h-full flex-1 cursor-pointer flex-col items-center justify-end"
                onMouseEnter={() => onHover(s.number)}
                onMouseLeave={() => onHover(null)}
              >
                <div
                  className={cn(
                    "relative w-full max-w-12 rounded-t-md transition-[height,transform,box-shadow,opacity] duration-700 ease-out",
                    isHovered && "-translate-y-1",
                    hovered !== null && !isHovered && "opacity-50",
                  )}
                  style={{
                    height: grown ? y(s.fraction) : "0%",
                    transitionDelay: grown ? `${i * 55}ms` : "0ms",
                    background: `repeating-linear-gradient(${s.status === "ok" ? 45 : -45}deg, ${color} 0 2.5px, ${color}26 2.5px 7px)`,
                    boxShadow: isHovered ? `0 0 0 2px ${color}, 0 8px 18px -6px ${color}` : `inset 0 0 0 1px ${color}`,
                  }}
                >
                  <div
                    className={cn(
                      "absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold tabular-nums",
                      s.status === "small" ? "text-red-600 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400",
                    )}
                  >
                    {formatNumber(s.fraction * 100, { maximumFractionDigits: 1, minimumFractionDigits: 1 })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-1 flex gap-1.5 px-2">
        {slices.map((s) => (
          <div
            key={s.number}
            className={cn(
              "flex-1 text-center text-[10px] tabular-nums text-muted-foreground transition-colors",
              hovered === s.number && "font-semibold text-foreground",
            )}
          >
            {s.number}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Overlay / source switch over the photo (keyboard: S). */
export function SlicingModeToggle({
  mode,
  onChange,
  className,
}: {
  mode: SlicingViewMode;
  onChange: (mode: SlicingViewMode) => void;
  className?: string;
}) {
  const { t } = useLocalization();
  const options: { value: SlicingViewMode; label: string; icon: React.ReactNode }[] = [
    { value: "overlay", label: t("slicing.modeOverlay"), icon: <Scissors className="h-3.5 w-3.5" /> },
    { value: "source", label: t("slicing.modeSource"), icon: <ImageIcon className="h-3.5 w-3.5" /> },
  ];
  return (
    <div
      className={cn(
        "slicing-legend pointer-events-auto absolute bottom-3 right-3 z-30 flex items-center gap-1 rounded-full",
        "border border-white/15 bg-slate-950/70 p-1 text-xs text-white shadow-xl backdrop-blur-md",
        className,
      )}
    >
      <div className="relative flex">
        <span
          className="absolute inset-y-0 left-0 w-1/2 rounded-full bg-white shadow transition-transform duration-300 ease-out"
          style={{ transform: mode === "source" ? "translateX(100%)" : "none" }}
        />
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "relative z-10 flex w-9 items-center justify-center gap-1.5 rounded-full px-2.5 py-1 font-medium transition-colors duration-300 sm:w-[5.6rem]",
              mode === o.value ? "text-slate-900" : "text-white/75 hover:text-white",
            )}
            aria-pressed={mode === o.value}
            aria-label={o.label}
          >
            {o.icon}
            <span className="hidden sm:inline">{o.label}</span>
          </button>
        ))}
      </div>
      <kbd className="mx-1 hidden rounded border border-white/20 bg-white/10 px-1.5 text-[10px] font-semibold text-white/70 sm:block">
        S
      </kbd>
    </div>
  );
}

/** top-left of a size x size lens kept inside the w x h area, and the cursor's position inside the lens */
function lensPlacement(pos: { x: number; y: number }, w: number, h: number, size: number) {
  const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));
  const left = clamp(pos.x - size / 2, 4, w - size - 4);
  const top = clamp(pos.y - size / 2, 4, h - size - 4);
  return { left, top, cx: pos.x - left, cy: pos.y - top };
}
const lensBox = (pos: { x: number; y: number }, el: HTMLElement | null, size: number) => {
  const { left, top } = lensPlacement(pos, el?.clientWidth ?? 0, el?.clientHeight ?? 0, size);
  return { left, top };
};
const lensCross = (pos: { x: number; y: number }, el: HTMLElement | null, size: number) =>
  lensPlacement(pos, el?.clientWidth ?? 0, el?.clientHeight ?? 0, size);

/**
 * Magnifier for the source view: the full-resolution photo under the cursor (wheel = zoom), with the detected
 * crossings as small ticks outside the rim. Draws from the original image, so the cut is seen at native detail.
 */
export function SlicingLoupe({
  image,
  frame,
  slicing,
  size = 260,
}: {
  image: HTMLImageElement;
  frame: SlicingFrame;
  slicing: SlicingModelInfo;
  size?: number;
}) {
  const { t, formatNumber } = useLocalization();
  const areaRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [zoom, setZoom] = useState(3);

  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((z) => Math.min(12, Math.max(1.5, z * (e.deltaY < 0 ? 1.15 : 1 / 1.15))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    const el = areaRef.current;
    const canvas = canvasRef.current;
    if (!el || !canvas || !pos) return;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    const s = Math.min(cw / frame.width, ch / frame.height); // displayed px per source px (object-contain)
    const ox = (cw - frame.width * s) / 2;
    const oy = (ch - frame.height * s) / 2;
    const sx = frame.x + (pos.x - ox) / s;
    const sy = frame.y + (pos.y - oy) / s;
    const dpr = window.devicePixelRatio || 1;
    const px = Math.round(size * dpr);
    if (canvas.width !== px) {
      canvas.width = px;
      canvas.height = px;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const side = size / (s * zoom); // source px across the lens
    const k = px / side;
    // the lens stays inside the photo; the cursor point sits under the crosshair, wherever that is in the lens
    const { cx: lx, cy: ly } = lensPlacement(pos, cw, ch, size);
    const x0 = sx - side * (lx / size);
    const y0 = sy - side * (ly / size);
    ctx.fillStyle = "#0b0f19";
    ctx.fillRect(0, 0, px, px);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, x0, y0, side, side, 0, 0, px, px);
    // detected crossings: ticks just outside the rim
    const circle = slicing.circle;
    if (circle) {
      ctx.lineCap = "round";
      for (const c of slicing.cuts ?? []) {
        const a = rad(c.angle);
        const at = (r: number): [number, number] => [
          (circle.x + r * circle.r * Math.cos(a) - x0) * k,
          (circle.y + r * circle.r * Math.sin(a) - y0) * k,
        ];
        const [ax, ay] = at(1.015);
        const [bx, by] = at(1.12);
        for (const [color, w] of [
          ["rgba(15,23,42,0.75)", 5],
          ["#ffffff", 2.5],
        ] as const) {
          ctx.strokeStyle = color;
          ctx.lineWidth = w * dpr;
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.lineTo(bx, by);
          ctx.stroke();
        }
      }
    }
  }, [pos, zoom, image, frame, slicing, size]);

  return (
    <div
      ref={areaRef}
      className="absolute inset-0 z-[25] cursor-crosshair"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
      }}
      onMouseLeave={() => setPos(null)}
    >
      {pos ? (
        <div
          className="pointer-events-none absolute overflow-hidden rounded-full shadow-[0_18px_50px_-12px_rgba(0,0,0,0.65)] ring-2 ring-white/90 animate-in fade-in-0 zoom-in-90 duration-200"
          style={{ ...lensBox(pos, areaRef.current, size), width: size, height: size }}
        >
          <canvas ref={canvasRef} className="h-full w-full" />
          <div
            className="absolute h-4 w-px -translate-x-1/2 -translate-y-1/2 bg-white/80"
            style={{ left: lensCross(pos, areaRef.current, size).cx, top: lensCross(pos, areaRef.current, size).cy }}
          />
          <div
            className="absolute h-px w-4 -translate-x-1/2 -translate-y-1/2 bg-white/80"
            style={{ left: lensCross(pos, areaRef.current, size).cx, top: lensCross(pos, areaRef.current, size).cy }}
          />
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-slate-950/70 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-white">
            {formatNumber(zoom, { maximumFractionDigits: 1, minimumFractionDigits: 1 })}×
          </div>
        </div>
      ) : (
        <div className="slicing-legend pointer-events-none absolute right-3 top-14 rounded-full bg-slate-950/60 px-2.5 py-1 text-[11px] text-white/85 backdrop-blur">
          {t("slicing.loupeHint")}
        </div>
      )}
    </div>
  );
}
